import { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { LayoutDashboard, Shirt, ClipboardList, Clock, Store, Plus, Trash2, Pencil, PoundSterling, AlertCircle, CreditCard, MessageSquare, Send, ExternalLink, CheckCircle2, Users, Ticket, UserCog } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ImageUploadField from "@/components/ImageUploadField";
import GalleryField from "@/components/GalleryField";

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "items", label: "Price list", icon: Shirt },
  { key: "orders", label: "Orders", icon: ClipboardList },
  { key: "customers", label: "Customers", icon: Users },
  { key: "coupons", label: "Coupons", icon: Ticket },
  { key: "driverpay", label: "Driver payouts", icon: CreditCard },
  { key: "complaints", label: "Driver issues", icon: MessageSquare },
  { key: "schedule", label: "Schedule", icon: Clock },
  { key: "profile", label: "Shop profile", icon: Store },
  { key: "account", label: "My account", icon: UserCog },
];
const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const ORDER_STATUSES = ["pending", "confirmed", "cleaning", "ready", "out_for_delivery", "delivered", "cancelled"];

export default function ManagerDashboard() {
  const [active, setActive] = useState("overview");
  const [stats, setStats] = useState(null);
  const [laundry, setLaundry] = useState(null);
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [defaults, setDefaults] = useState([]);

  const reloadItems = () => api.get("/manager/items").then((r) => setItems(r.data));
  const reloadLaundry = () => api.get("/manager/laundry").then((r) => setLaundry(r.data));

  useEffect(() => {
    api.get("/manager/stats").then((r) => setStats(r.data)).catch(() => {});
    reloadLaundry();
    reloadItems();
    api.get("/manager/orders").then((r) => setOrders(r.data));
    api.get("/clothing-types").then((r) => setDefaults(r.data));
  }, []);

  return (
    <DashboardShell title="Laundry Manager" nav={NAV} active={active} onSelect={setActive}>
      {active === "overview" && <Overview stats={stats} laundry={laundry} />}
      {active === "items" && <Items items={items} defaults={defaults} reload={reloadItems} />}
      {active === "orders" && <Orders orders={orders} smsEnabled={!!stats?.plan?.capabilities?.sms} reload={() => api.get("/manager/orders").then((r) => setOrders(r.data))} />}
      {active === "customers" && <Customers />}
      {active === "coupons" && <ManagerCoupons />}
      {active === "driverpay" && <DriverPayouts />}
      {active === "complaints" && <ManagerComplaints />}
      {active === "schedule" && laundry && <Schedule laundry={laundry} reload={reloadLaundry} />}
      {active === "profile" && laundry && <Profile laundry={laundry} reload={reloadLaundry} />}
      {active === "account" && <ManagerAccount />}
    </DashboardShell>
  );
}

function Customers() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/manager/customers").then((r) => setRows(r.data)); }, []);
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Customers</h1>
      <p className="mt-1 text-slate-500">Everyone who has ordered from your laundry, ranked by spend. Click a customer to see their orders.</p>
      <div className="mt-6 space-y-3">
        {rows.length === 0 && <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-400">No customers yet.</div>}
        {rows.map((c) => (
          <div key={c.customer_id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`customer-${c.customer_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 cursor-pointer" onClick={() => setOpen(open === c.customer_id ? null : c.customer_id)}>
              <div>
                <p className="font-heading text-lg font-medium text-slate-900">{c.name}</p>
                <p className="mt-0.5 text-sm text-slate-500">{c.email || "—"} · {c.phone || "no phone"}</p>
              </div>
              <div className="text-right">
                <p className="font-heading text-lg font-semibold text-slate-900">£{c.total_spent.toFixed(2)}</p>
                <p className="text-xs text-slate-400">{c.order_count} order(s)</p>
              </div>
            </div>
            {open === c.customer_id && (
              <div className="mt-3 space-y-1 border-t border-slate-100 pt-3" data-testid={`customer-orders-${c.customer_id}`}>
                {c.orders.map((o) => (
                  <div key={o.id} className="flex items-center justify-between text-sm text-slate-600">
                    <span className="font-mono text-xs">#{o.id.slice(0, 8)} · {(o.created_at || "").slice(0, 10)}</span>
                    <span className="capitalize">{o.status.replace(/_/g, " ")}</span>
                    <span><Badge variant="outline" className={o.payment_status === "paid" ? "border-green-300 text-green-700" : "border-amber-300 text-amber-700"}>{o.payment_status}</Badge></span>
                    <span className="font-mono">£{o.total.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ManagerCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [claims, setClaims] = useState({ claimable: [], claimable_total: 0, claims: [] });
  const [f, setF] = useState({ code: "", discount_type: "percent", value: "", max_uses: "", expires_at: "" });
  const loadCoupons = () => api.get("/manager/coupons").then((r) => setCoupons(r.data));
  const loadClaims = () => api.get("/manager/coupon-claims").then((r) => setClaims(r.data));
  useEffect(() => { loadCoupons(); loadClaims(); }, []);
  const add = async () => {
    if (!f.code || !f.value) return toast.error("Code and value required");
    try {
      await api.post("/manager/coupons", { code: f.code, discount_type: f.discount_type, value: parseFloat(f.value), active: true, max_uses: f.max_uses ? parseInt(f.max_uses) : 0, expires_at: f.expires_at || null });
      loadCoupons(); setF({ code: "", discount_type: "percent", value: "", max_uses: "", expires_at: "" }); toast.success("Coupon created");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (c) => { await api.put(`/manager/coupons/${c.id}`, { code: c.code, discount_type: c.discount_type, value: c.value, active: !c.active, max_uses: c.max_uses, expires_at: c.expires_at }); loadCoupons(); };
  const del = async (id) => { await api.delete(`/manager/coupons/${id}`); loadCoupons(); toast.success("Deleted"); };
  const claimAll = async () => {
    try { await api.post("/manager/coupon-claims", { order_ids: claims.claimable.map((c) => c.order_id) }); loadClaims(); toast.success("Claim requested — admin will settle it"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Your coupons</h1>
      <p className="mt-1 text-slate-500">Your coupons only work at your laundry and simply reduce the customer's price.</p>
      <div className="mt-6 flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4">
        <div className="space-y-1.5"><Label className="text-xs">Code</Label><Input className="h-9 w-32 uppercase" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="mgr-coupon-code" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Type</Label>
          <Select value={f.discount_type} onValueChange={(v) => setF({ ...f, discount_type: v })}>
            <SelectTrigger className="h-9 w-28" data-testid="mgr-coupon-type"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="percent">Percent %</SelectItem><SelectItem value="fixed">Fixed £</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label className="text-xs">Value</Label><Input className="h-9 w-24" type="number" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} data-testid="mgr-coupon-value" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Max uses</Label><Input className="h-9 w-24" type="number" value={f.max_uses} onChange={(e) => setF({ ...f, max_uses: e.target.value })} /></div>
        <div className="space-y-1.5"><Label className="text-xs">Expires</Label><Input className="h-9 w-40" type="date" value={f.expires_at} onChange={(e) => setF({ ...f, expires_at: e.target.value })} /></div>
        <Button className="h-9 bg-brand hover:bg-brand-hover" onClick={add} data-testid="mgr-coupon-save"><Plus className="mr-1.5 h-4 w-4" /> Add</Button>
      </div>
      <div className="mt-4 space-y-2">
        {coupons.length === 0 && <p className="text-sm text-slate-400">No coupons yet.</p>}
        {coupons.map((c) => (
          <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid={`mgr-coupon-${c.id}`}>
            <div className="flex items-center gap-3"><span className="font-mono font-bold">{c.code}</span><Badge variant="secondary">{c.discount_type === "percent" ? `${c.value}% off` : `£${c.value} off`}</Badge><span className="text-xs text-slate-400">{c.used_count} used{!c.active ? " · disabled" : ""}</span></div>
            <div className="flex items-center gap-1"><Button size="sm" variant="ghost" onClick={() => toggle(c)}>{c.active ? "Disable" : "Enable"}</Button><Button size="sm" variant="ghost" className="text-destructive" onClick={() => del(c.id)} data-testid={`mgr-coupon-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button></div>
          </div>
        ))}
      </div>

      <h2 className="mt-10 font-heading text-xl font-bold text-slate-900">Admin coupon claim-back</h2>
      <p className="mt-1 text-sm text-slate-500">When a customer used a platform (admin) coupon at your shop, you can reclaim that discount from the admin.</p>
      <div className="mt-4 rounded-xl border border-slate-200 bg-white p-5" data-testid="claim-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm text-slate-500">Claimable now</p>
            <p className="font-heading text-2xl font-bold text-slate-900" data-testid="claimable-total">£{claims.claimable_total.toFixed(2)}</p>
            <p className="text-xs text-slate-400">{claims.claimable.length} order(s) with admin coupons</p>
          </div>
          <Button className="bg-brand hover:bg-brand-hover" disabled={claims.claimable.length === 0} onClick={claimAll} data-testid="request-claim-btn">Request claim-back</Button>
        </div>
        {claims.claims.length > 0 && (
          <div className="mt-4 space-y-1 border-t border-slate-100 pt-3">
            {claims.claims.map((c) => (
              <div key={c.id} className="flex justify-between text-sm text-slate-600"><span>£{c.amount.toFixed(2)} · {(c.requested_at || "").slice(0, 10)} · {(c.order_ids || []).length} orders</span><Badge variant="outline" className={c.status === "settled" ? "border-green-300 text-green-700" : "border-amber-300 text-amber-700"}>{c.status}</Badge></div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DriverPayouts() {
  const [d, setD] = useState({ owed_total: 0, payouts: [] });
  const load = () => api.get("/manager/driver-payouts").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  const pay = async (id) => { await api.post(`/manager/driver-payouts/${id}/pay`, { note: "" }); load(); toast.success("Driver paid"); };
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Driver payouts</h1>
      <p className="mt-1 text-slate-500">You pay the drivers who collect & deliver for your laundry. Mark each request paid once you've transferred it.</p>
      <div className="mt-4 rounded-xl border border-slate-200 bg-white p-5">
        <p className="text-sm text-slate-500">Outstanding (unpaid completed jobs)</p>
        <p className="font-heading text-2xl font-bold text-slate-900" data-testid="owed-total">£{d.owed_total.toFixed(2)}</p>
      </div>
      <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-bold uppercase tracking-[0.1em] text-slate-400">
            <tr><th className="px-5 py-3">Driver</th><th className="px-5 py-3">Requested</th><th className="px-5 py-3">Jobs</th><th className="px-5 py-3 text-right">Amount</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Action</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {d.payouts.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-400">No payout requests yet.</td></tr>}
            {d.payouts.map((p) => (
              <tr key={p.id} data-testid={`mgr-payout-${p.id}`}>
                <td className="px-5 py-3 font-medium text-slate-900">{p.driver_name}</td>
                <td className="px-5 py-3 text-slate-500">{(p.requested_at || "").slice(0, 10)}</td>
                <td className="px-5 py-3 text-slate-500">{(p.job_ids || []).length}</td>
                <td className="px-5 py-3 text-right font-mono">£{p.amount.toFixed(2)}</td>
                <td className="px-5 py-3"><Badge variant="outline" className={p.status === "paid" ? "border-green-300 text-green-700" : "border-amber-300 text-amber-700"}>{p.status}</Badge></td>
                <td className="px-5 py-3 text-right">{p.status !== "paid" && <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => pay(p.id)} data-testid={`mgr-pay-${p.id}`}>Mark paid</Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ManagerComplaints() {
  const [rows, setRows] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [f, setF] = useState({ against_id: "", subject: "", message: "" });
  const load = () => api.get("/manager/complaints").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/manager/drivers").then((r) => setDrivers(r.data)); }, []);
  const submit = async () => {
    if (!f.against_id || !f.subject || !f.message) return toast.error("Pick a driver and add subject + message");
    const dr = drivers.find((d) => d.id === f.against_id);
    try {
      await api.post("/complaints", { against_type: "driver", against_id: f.against_id, against_name: dr?.name || "Driver", subject: f.subject, message: f.message });
      toast.success("Issue raised about the driver"); setF({ against_id: "", subject: "", message: "" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Driver issues</h1>
      <p className="mt-1 text-slate-500">Raise a problem about a driver who worked for you. Our team reviews every report.</p>
      <div className="mt-6 space-y-3 rounded-xl border border-slate-200 bg-white p-5">
        <Select value={f.against_id} onValueChange={(v) => setF({ ...f, against_id: v })}>
          <SelectTrigger data-testid="complaint-driver-select"><SelectValue placeholder="Select a driver" /></SelectTrigger>
          <SelectContent>{drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
        </Select>
        <Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="Subject" data-testid="complaint-subject" />
        <Textarea rows={4} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="Describe the issue…" data-testid="complaint-message" />
        <Button onClick={submit} className="bg-brand hover:bg-brand-hover" data-testid="complaint-submit">Raise issue</Button>
      </div>
      <div className="mt-6 space-y-3">
        {rows.map((c) => (
          <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`mgr-complaint-${c.id}`}>
            <div className="flex items-center justify-between">
              <p className="font-medium text-slate-900">{c.subject} <span className="text-xs text-slate-400">· {c.raised_by_role === "customer" ? "from customer" : "by you"} · about {c.against_type}</span></p>
              <Badge variant="outline" className={c.status === "resolved" ? "border-green-300 text-green-700" : "border-amber-300 text-amber-700"}>{c.status}</Badge>
            </div>
            <p className="mt-1 text-sm text-slate-600">{c.message}</p>
            {c.admin_response && <p className="mt-2 rounded-lg bg-slate-50 p-2 text-sm text-slate-600"><b>Admin:</b> {c.admin_response}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}


function ManagerAccount() {
  const { user } = useAuth();
  const [f, setF] = useState({ name: user?.name || "", email: user?.email || "", current_password: "", new_password: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try {
      const payload = { name: f.name, email: f.email };
      if (f.new_password) { payload.current_password = f.current_password; payload.new_password = f.new_password; }
      await api.put("/auth/profile", payload); toast.success("Account updated"); setF({ ...f, current_password: "", new_password: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="max-w-xl">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">My account</h1>
      <p className="mt-1 text-slate-500">Update your login name, email and password.</p>
      <div className="mt-6 space-y-4 rounded-xl border border-slate-200 bg-white p-6">
        <div className="space-y-1.5"><Label>Name</Label><Input value={f.name} onChange={set("name")} data-testid="mgr-account-name" /></div>
        <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={f.email} onChange={set("email")} data-testid="mgr-account-email" /></div>
        <div className="border-t border-slate-100 pt-4 text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Change password (optional)</div>
        <div className="space-y-1.5"><Label>Current password</Label><Input type="password" value={f.current_password} onChange={set("current_password")} data-testid="mgr-account-current" /></div>
        <div className="space-y-1.5"><Label>New password</Label><Input type="password" value={f.new_password} onChange={set("new_password")} data-testid="mgr-account-new" /></div>
        <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-hover" data-testid="mgr-account-save">Save changes</Button>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">{label}</p>
        <Icon className={`h-5 w-5 ${accent || "text-brand"}`} strokeWidth={1.5} />
      </div>
      <p className="mt-3 font-heading text-3xl font-bold text-slate-900">{value}</p>
    </div>
  );
}

function Overview({ stats, laundry }) {
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Overview</h1>
      {laundry?.status !== "approved" && (
        <div className="mt-4 flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="approval-banner">
          <AlertCircle className="h-5 w-5 shrink-0" />
          Your shop is <b>{laundry?.status}</b>. It won't appear in customer search until an admin approves it. Complete your profile to speed things up.
        </div>
      )}
      <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={PoundSterling} label="Revenue" value={`£${(stats?.revenue ?? 0).toFixed(2)}`} accent="text-green-600" />
        <StatCard icon={ClipboardList} label="Total orders" value={stats?.total_orders ?? 0} />
        <StatCard icon={ClipboardList} label="Paid orders" value={stats?.paid_orders ?? 0} />
        <StatCard icon={Shirt} label="Price list items" value={stats?.items ?? 0} />
      </div>
      {stats?.plan && <PlanCard plan={stats.plan} />}
      <ConnectCard />
    </div>
  );
}

function PlanCard({ plan }) {
  const pkg = plan.package || {};
  const limit = plan.limit || 0;
  const used = plan.used || 0;
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const caps = plan.capabilities || {};
  const capList = [
    ["SMS notifications", caps.sms],
    ["Driver dispatch", caps.drivers],
    ["Custom garments", caps.custom_items],
    ["Analytics", caps.analytics],
    ["Multi-location", caps.multi_location],
  ];
  return (
    <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6" data-testid="plan-card">
      {plan.over_limit && (
        <div className="mb-4 flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="limit-banner">
          <AlertCircle className="h-5 w-5 shrink-0" />
          You've reached your <b>{pkg.name}</b> plan limit of {limit} orders this month. Bookings still come through — contact the platform admin to upgrade for more capacity & features.
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Current plan</p>
          <p className="mt-1 font-heading text-xl font-medium text-slate-900">{pkg.name || "—"}</p>
        </div>
        <div className="text-right">
          <p className="font-mono text-sm text-slate-500">{used}{limit > 0 ? ` / ${limit}` : ""} orders this month</p>
          {limit > 0 && (
            <div className="mt-2 h-2 w-40 overflow-hidden rounded-full bg-slate-100">
              <div className={`h-full rounded-full ${plan.over_limit ? "bg-amber-500" : "bg-brand"}`} style={{ width: `${pct}%` }} />
            </div>
          )}
          {limit === 0 && <p className="text-xs text-green-600">Unlimited orders</p>}
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
        {capList.map(([label, on]) => (
          <span key={label} className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${on ? "bg-accent text-brand" : "bg-slate-100 text-slate-400 line-through"}`}>
            <CheckCircle2 className="h-3.5 w-3.5" /> {label}
          </span>
        ))}
      </div>
    </div>
  );
}

function ConnectCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/manager/stripe/connect/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onboard = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/manager/stripe/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setLoading(false);
    }
  };

  const ready = status?.charges_enabled;
  return (
    <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6" data-testid="connect-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent text-brand"><CreditCard className="h-5 w-5" strokeWidth={1.5} /></span>
          <div>
            <h3 className="font-heading text-lg font-medium text-slate-900">Stripe payouts</h3>
            <p className="mt-1 max-w-md text-sm text-slate-500">
              Connect your Stripe account to receive customer payments directly. The platform commission is deducted automatically.
            </p>
            {status && (
              <div className="mt-2">
                {!status.platform_configured && <span className="text-sm text-amber-600">Platform Stripe isn't configured by the admin yet.</span>}
                {status.platform_configured && ready && <span className="flex items-center gap-1.5 text-sm font-medium text-green-600"><CheckCircle2 className="h-4 w-4" /> Connected · ready to accept payments</span>}
                {status.platform_configured && status.connected && !ready && <span className="text-sm text-amber-600">Onboarding incomplete — finish your Stripe details.</span>}
                {status.platform_configured && !status.connected && <span className="text-sm text-slate-500">Not connected yet.</span>}
              </div>
            )}
          </div>
        </div>
        {status?.platform_configured && !ready && (
          <Button onClick={onboard} disabled={loading} className="bg-brand hover:bg-brand-hover" data-testid="connect-stripe-btn">
            <ExternalLink className="mr-2 h-4 w-4" /> {status?.connected ? "Continue onboarding" : "Connect with Stripe"}
          </Button>
        )}
      </div>
    </div>
  );
}

function ItemDialog({ trigger, onSave, defaults, initial }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial || { name: "", price: "", category: "General", clothing_type_id: "" });

  const save = async () => {
    if (!form.name || form.price === "") return toast.error("Name and price required");
    try {
      await onSave({ ...form, price: parseFloat(form.price) });
      setOpen(false);
      if (!initial) setForm({ name: "", price: "", category: "General", clothing_type_id: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{initial ? "Edit item" : "Add item"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {!initial && (
            <div className="space-y-1.5">
              <Label>Quick add from default catalogue</Label>
              <Select onValueChange={(v) => { const d = defaults.find((x) => x.id === v); setForm({ ...form, name: d.name, category: d.category, clothing_type_id: d.id }); }}>
                <SelectTrigger data-testid="default-select"><SelectValue placeholder="Choose a default garment…" /></SelectTrigger>
                <SelectContent>{defaults.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} · {d.category}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5"><Label>Item name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="item-name-input" placeholder="e.g. Silk Blouse" /></div>
          <div className="space-y-1.5"><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="item-category-input" /></div>
          <div className="space-y-1.5"><Label>Price (£)</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="item-price-input" placeholder="3.50" /></div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-brand hover:bg-brand-hover" data-testid="save-item-btn">Save item</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Items({ items, defaults, reload }) {
  const add = async (data) => { await api.post("/manager/items", data); await reload(); toast.success("Item added"); };
  const update = (id) => async (data) => { await api.put(`/manager/items/${id}`, data); await reload(); toast.success("Item updated"); };
  const del = async (id) => { await api.delete(`/manager/items/${id}`); await reload(); toast.success("Item removed"); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Price list</h1>
        <ItemDialog defaults={defaults} onSave={add} trigger={<Button className="bg-brand hover:bg-brand-hover" data-testid="add-item-btn"><Plus className="mr-2 h-4 w-4" /> Add item</Button>} />
      </div>
      <div className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-bold uppercase tracking-[0.1em] text-slate-400">
            <tr><th className="px-6 py-3">Item</th><th className="px-6 py-3">Category</th><th className="px-6 py-3 text-right">Price</th><th className="px-6 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 && <tr><td colSpan={4} className="px-6 py-10 text-center text-slate-400">No items yet. Add your first garment.</td></tr>}
            {items.map((it) => (
              <tr key={it.id} className="transition-colors hover:bg-slate-50" data-testid={`manager-item-${it.id}`}>
                <td className="px-6 py-3 font-medium text-slate-900">{it.name}</td>
                <td className="px-6 py-3 text-slate-500">{it.category}</td>
                <td className="px-6 py-3 text-right font-mono">£{it.price.toFixed(2)}</td>
                <td className="px-6 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <ItemDialog defaults={defaults} initial={{ name: it.name, category: it.category, price: String(it.price), clothing_type_id: it.clothing_type_id || "" }} onSave={update(it.id)} trigger={<Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`edit-item-${it.id}`}><Pencil className="h-4 w-4" /></Button>} />
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => del(it.id)} data-testid={`delete-item-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SmsDialog({ orderId, customerName }) {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!msg.trim()) return toast.error("Enter a message");
    setSending(true);
    try { await api.post(`/manager/orders/${orderId}/sms`, { message: msg }); toast.success("SMS sent"); setOpen(false); setMsg(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="secondary" data-testid={`sms-btn-${orderId}`}><MessageSquare className="mr-1.5 h-4 w-4" /> SMS</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Text {customerName}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Your laundry is ready for collection!" data-testid="sms-message-input" />
          <p className="text-xs text-slate-400">Sent via the platform's shared Twilio number (configured by the site admin).</p>
        </div>
        <DialogFooter><Button onClick={send} disabled={sending} className="bg-brand hover:bg-brand-hover" data-testid="send-sms-btn"><Send className="mr-2 h-4 w-4" /> Send SMS</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Orders({ orders, reload, smsEnabled }) {
  const setStatus = async (id, status) => { await api.put(`/manager/orders/${id}/status`, { status }); await reload(); toast.success("Status updated"); };
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Orders</h1>
      {!smsEnabled && <p className="mt-1 text-sm text-slate-400">Upgrade your plan to text customers directly from each order.</p>}
      <div className="mt-6 space-y-4">
        {orders.length === 0 && <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-400">No orders yet.</div>}
        {orders.map((o) => (
          <div key={o.id} className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`manager-order-${o.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-medium text-slate-900">{o.customer_name} <span className="font-mono text-xs text-slate-400">#{o.id.slice(0, 8)}</span></p>
                <p className="mt-1 text-sm text-slate-500">{o.pickup_date} · {o.pickup_slot} · {o.address}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className={o.payment_status === "paid" ? "border-green-300 text-green-700" : "border-amber-300 text-amber-700"}>{o.payment_status}</Badge>
                {smsEnabled && <SmsDialog orderId={o.id} customerName={o.customer_name} />}
                <Select value={o.status} onValueChange={(v) => setStatus(o.id, v)}>
                  <SelectTrigger className="h-9 w-44" data-testid={`order-status-select-${o.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{ORDER_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
              {o.items.map((li, i) => <Badge key={i} variant="secondary">{li.qty} × {li.name}</Badge>)}
              <span className="ml-auto font-heading text-lg font-semibold text-slate-900">£{o.total.toFixed(2)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Schedule({ laundry, reload }) {
  const [sched, setSched] = useState(laundry.schedule || {});
  const [saving, setSaving] = useState(false);
  const upd = (day, field, val) => setSched({ ...sched, [day]: { ...sched[day], [field]: val } });
  const save = async () => { setSaving(true); await api.put("/manager/laundry", { schedule: sched }); await reload(); setSaving(false); toast.success("Schedule saved"); };
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Opening hours</h1>
      <div className="mt-6 max-w-2xl space-y-3 rounded-xl border border-slate-200 bg-white p-6">
        {DAYS.map((d) => (
          <div key={d} className="flex items-center gap-4" data-testid={`schedule-${d}`}>
            <span className="w-28 text-sm font-medium capitalize text-slate-700">{d}</span>
            <label className="flex items-center gap-2 text-sm text-slate-500">
              <input type="checkbox" checked={!sched[d]?.closed} onChange={(e) => upd(d, "closed", !e.target.checked)} className="h-4 w-4 accent-[#2563EB]" /> Open
            </label>
            {!sched[d]?.closed && (
              <div className="flex items-center gap-2">
                <Input type="time" value={sched[d]?.open || "08:00"} onChange={(e) => upd(d, "open", e.target.value)} className="h-9 w-32" />
                <span className="text-slate-400">–</span>
                <Input type="time" value={sched[d]?.close || "18:00"} onChange={(e) => upd(d, "close", e.target.value)} className="h-9 w-32" />
              </div>
            )}
          </div>
        ))}
        <Button onClick={save} disabled={saving} className="mt-2 bg-brand hover:bg-brand-hover" data-testid="save-schedule-btn">Save schedule</Button>
      </div>
    </div>
  );
}

function Profile({ laundry, reload }) {
  const [form, setForm] = useState({ ...laundry });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/manager/laundry", {
        name: form.name, description: form.description, address: form.address, city: form.city,
        postcode: form.postcode, phone: form.phone,
        lat: form.lat ? parseFloat(form.lat) : null, lng: form.lng ? parseFloat(form.lng) : null,
        stripe_account: form.stripe_account,
        max_orders_per_slot: form.max_orders_per_slot ? parseInt(form.max_orders_per_slot) : 0,
        cover_image: form.cover_image || "",
        gallery: form.gallery || [],
        min_turnaround_hours: form.min_turnaround_hours === "" || form.min_turnaround_hours == null ? 24 : parseInt(form.min_turnaround_hours),
      });
      await reload();
      toast.success("Profile saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save profile"); }
    finally { setSaving(false); }
  };
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Shop profile</h1>
      <div className="mt-6 max-w-2xl space-y-4 rounded-xl border border-slate-200 bg-white p-6">
        <ImageUploadField label="Cover photo (shown on your search card and booking page)" value={form.cover_image} onChange={(v) => setForm({ ...form, cover_image: v })} hideUrl testid="shop-cover" uploadedMsg="Photo uploaded — save your profile to publish it" />
        <GalleryField label="Shop photos customers can swipe through on your booking page — save profile to publish" value={form.gallery} onChange={(v) => setForm({ ...form, gallery: v })} testid="shop-gallery" />
        <div className="space-y-1.5"><Label>Shop name</Label><Input value={form.name || ""} onChange={set("name")} data-testid="shop-name-input" /></div>
        <div className="space-y-1.5"><Label>Description</Label><Textarea value={form.description || ""} onChange={set("description")} data-testid="shop-desc-input" /></div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5"><Label>Address</Label><Input value={form.address || ""} onChange={set("address")} data-testid="shop-address-input" /></div>
          <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone || ""} onChange={set("phone")} data-testid="shop-phone-input" /></div>
          <div className="space-y-1.5"><Label>City</Label><Input value={form.city || ""} onChange={set("city")} data-testid="shop-city-input" /></div>
          <div className="space-y-1.5"><Label>Postcode</Label><Input value={form.postcode || ""} onChange={set("postcode")} data-testid="shop-postcode-input" /></div>
          <div className="space-y-1.5"><Label>Latitude</Label><Input value={form.lat ?? ""} onChange={set("lat")} placeholder="51.5074" data-testid="shop-lat-input" /></div>
          <div className="space-y-1.5"><Label>Longitude</Label><Input value={form.lng ?? ""} onChange={set("lng")} placeholder="-0.1278" data-testid="shop-lng-input" /></div>
        </div>
        <div className="space-y-1.5"><Label>Your Stripe account ID (to receive payouts)</Label><Input value={form.stripe_account || ""} onChange={set("stripe_account")} placeholder="acct_…" data-testid="shop-stripe-input" /></div>
        <div className="space-y-1.5"><Label>Max orders per pickup slot (0 = unlimited)</Label><Input type="number" min="0" value={form.max_orders_per_slot ?? ""} onChange={set("max_orders_per_slot")} placeholder="e.g. 5" data-testid="shop-slot-cap-input" /><p className="text-xs text-slate-400">Prevents overbooking — customers can't book a slot once it's full.</p></div>
        <div className="space-y-1.5"><Label>Minimum hours between pickup and delivery</Label><Input type="number" min="0" max="720" value={form.min_turnaround_hours ?? 24} onChange={set("min_turnaround_hours")} data-testid="shop-turnaround-input" /><p className="text-xs text-slate-400">Customers can only choose a delivery slot at least this many hours after their pickup slot starts (default 24).</p></div>
        <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-hover" data-testid="save-profile-btn">Save profile</Button>
      </div>
    </div>
  );
}
